/**
 * The offline outbox (planning-app.md §4.4): writes a phone makes without a network — a comment,
 * a consent — wait in IndexedDB and are replayed in order when the connection is back, each with
 * the Idempotency-Key it was queued under, so a retry that already landed comes back as the same
 * answer instead of a duplicate. This module runs both in the page and in the service worker
 * (no DOM, no React): the page replays on `online`, the worker on Background Sync.
 */
export type OutboxKind = 'comment' | 'consent' | 'verification';

export interface OutboxEntry {
  /** Also the Idempotency-Key. */
  id: string;
  kind: OutboxKind;
  method: 'POST' | 'PUT';
  path: string;
  body: unknown;
  /** The CSRF token at queue time (the worker cannot read document.cookie). */
  csrf: string;
  /** What the entry is about, for the queue list: the decision id. */
  subjectId: string;
  /** A short preview for the queue list (the comment's first words). */
  preview: string;
  createdAt: string;
  attempts: number;
  lastError: string | null;
  /**
   * `queued` is replayed on every pass; `failed` has used its automatic attempts and waits for
   * the person to retry or discard it — the content is never dropped on its own. Entries written
   * before this field existed count as queued.
   */
  state?: 'queued' | 'failed';
}

export type ReplayResult =
  | { id: string; outcome: 'sent'; status: number }
  | { id: string; outcome: 'refused'; status: number; message: string }
  | { id: string; outcome: 'failed'; status: number; message: string }
  | { id: string; outcome: 'offline' };

/** The IndexedDB name holds queued writes on the person's device: it keeps its pre-v3.16 spelling, like the cookie names (HB24 D4). */
export const OUTBOX_DB = 'brain-outbox';
const STORE = 'entries';
export const IDEMPOTENCY_HEADER = 'idempotency-key';
/** After this many transient failures an entry stops being retried on its own and is shown as failed. */
export const OUTBOX_MAX_ATTEMPTS = 5;
/** Server answers a later retry can turn into a success; everything else under 500 is a final refusal. */
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(OUTBOX_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' }).createIndex('createdAt', 'createdAt');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        const req = run(store);
        let result: T;
        if (req) req.onsuccess = () => (result = req.result);
        t.oncomplete = () => {
          db.close();
          resolve(result);
        };
        t.onerror = () => {
          db.close();
          reject(t.error ?? new Error('indexedDB transaction failed'));
        };
      }),
  );
}

export function newOutboxId(): string {
  return `outbox-${crypto.randomUUID().replace(/-/g, '')}`;
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  const all = await tx<OutboxEntry[]>('readonly', (s) => s.getAll());
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function enqueueOutbox(entry: Omit<OutboxEntry, 'createdAt' | 'attempts' | 'lastError'>): Promise<OutboxEntry> {
  const full: OutboxEntry = { ...entry, createdAt: new Date().toISOString(), attempts: 0, lastError: null, state: 'queued' };
  await tx('readwrite', (s) => s.put(full));
  return full;
}

/** Puts a failed entry back in the queue, with its original key, for the next replay. */
export async function requeueOutbox(id: string): Promise<void> {
  const entry = await tx<OutboxEntry | undefined>('readonly', (s) => s.get(id));
  if (!entry) return;
  await tx('readwrite', (s) => s.put({ ...entry, state: 'queued', attempts: 0, lastError: null }));
}

/** Every failed entry back in the queue: the person asked for another try from the banner. */
export async function requeueFailedOutbox(): Promise<number> {
  const failed = (await listOutbox()).filter((e) => e.state === 'failed');
  for (const entry of failed) await tx('readwrite', (s) => s.put({ ...entry, state: 'queued', attempts: 0, lastError: null }));
  return failed.length;
}

export async function removeOutbox(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id));
}

export async function clearOutbox(): Promise<void> {
  await tx('readwrite', (s) => s.clear());
}

/** True for the failures a retry can fix: no network, the worker's own 503 `offline`, a gateway timeout. */
export function isNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError || (typeof err === 'object' && err !== null && (err as { status?: unknown }).status === 503 && (err as { code?: unknown }).code === 'offline');
}

/**
 * Sends every queued entry, oldest first, and stops at the first network failure (the rest wait
 * for the next pass). A refusal (4xx) is final: the entry is removed and reported, because the
 * same request will be refused again. A transient server failure (5xx, 408, 429) keeps the entry
 * with its key; after OUTBOX_MAX_ATTEMPTS it is marked failed and left to the person. Failed
 * entries are skipped here. Safe to run from the page and the worker at once: the server
 * serialises the two senders on the Idempotency-Key and the second gets a replay.
 */
export async function replayOutbox(fetchImpl: typeof fetch = fetch): Promise<ReplayResult[]> {
  const results: ReplayResult[] = [];
  for (const entry of await listOutbox()) {
    if (entry.state === 'failed') continue;
    let res: Response;
    try {
      res = await fetchImpl(entry.path, {
        method: entry.method,
        credentials: 'same-origin',
        headers: { accept: 'application/json', 'content-type': 'application/json', 'x-csrf-token': entry.csrf, [IDEMPOTENCY_HEADER]: entry.id },
        body: JSON.stringify(entry.body),
      });
    } catch {
      results.push({ id: entry.id, outcome: 'offline' });
      break;
    }
    if (res.ok) {
      await removeOutbox(entry.id);
      results.push({ id: entry.id, outcome: 'sent', status: res.status });
      continue;
    }
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      /* keep the status */
    }
    if (TRANSIENT_STATUSES.has(res.status)) {
      // The server or a gateway is unwell, or the worker is standing in for it: keep the entry and its key.
      const attempts = entry.attempts + 1;
      const exhausted = attempts >= OUTBOX_MAX_ATTEMPTS;
      await tx('readwrite', (s) => s.put({ ...entry, attempts, lastError: message, state: exhausted ? 'failed' : 'queued' }));
      results.push(exhausted ? { id: entry.id, outcome: 'failed', status: res.status, message } : { id: entry.id, outcome: 'offline' });
      break;
    }
    await removeOutbox(entry.id);
    results.push({ id: entry.id, outcome: 'refused', status: res.status, message });
  }
  return results;
}
